/**
 * AG product inventory: animates the stock line when the shopper changes size.
 * - The low stock count rolls from the previous number to the new one (7 -> 6 -> ... -> 2).
 * - The text fades in when the status changes (in stock / low / sold out).
 *
 * The theme (assets/product-inventory.js) morphs new server HTML into <product-inventory>.
 * The real values live in data-ag-status / data-ag-count (server rendered), so the
 * roll only rewrites the visible digits and never confuses its own edits with a new state.
 */
(() => {
  if (window.__agProductInventory) return;
  window.__agProductInventory = true;

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  const readState = (host) => {
    const status = host.querySelector('[data-ag-status]');
    const count = host.querySelector('[data-ag-count]');
    return {
      status: status ? status.getAttribute('data-ag-status') : '',
      count: count ? parseInt(count.getAttribute('data-ag-count'), 10) : null,
    };
  };

  const restartClass = (el, className) => {
    el.classList.remove(className);
    void el.offsetWidth; // restart the CSS animation
    el.classList.add(className);
  };

  const roll = (host, from, to) => {
    clearTimeout(host.__agRollTimer);
    const countEl = host.querySelector('[data-ag-count]');
    if (!countEl || from === null || from === to || reduceMotion.matches) return;

    const step = to > from ? 1 : -1;
    const delay = Math.max(45, Math.min(110, 420 / Math.abs(to - from)));
    let current = from;

    const tick = () => {
      if (!countEl.isConnected) return;
      countEl.textContent = String(current);
      restartClass(countEl, 'is-ticking');
      if (current === to) return;
      current += step;
      host.__agRollTimer = setTimeout(tick, delay);
    };

    tick();
  };

  const onChange = (host) => {
    const next = readState(host);
    const prev = host.__agState || { status: '', count: null };
    if (next.status === prev.status && next.count === prev.count) return; // our own digit edits

    host.__agState = next;

    if (next.status !== prev.status) {
      clearTimeout(host.__agRollTimer);
      const statusEl = host.querySelector('[data-ag-status]');
      if (statusEl && !reduceMotion.matches) restartClass(statusEl, 'is-changed');
    }

    // Low -> low with a different count: roll from the number that was showing
    if (next.status === 'low' && prev.status === 'low' && next.count !== null) {
      roll(host, prev.count, next.count);
    }
  };

  const setup = (host) => {
    if (host.__agObserver) return;
    host.__agState = readState(host);
    host.__agObserver = new MutationObserver(() => onChange(host));
    host.__agObserver.observe(host, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ['data-ag-status', 'data-ag-count'],
    });
  };

  const scan = () => document.querySelectorAll('product-inventory.ag-inv').forEach(setup);

  // New blocks: quick add dialogs, theme editor re-renders
  let queued = false;
  new MutationObserver(() => {
    if (queued) return;
    queued = true;
    requestAnimationFrame(() => {
      queued = false;
      scan();
    });
  }).observe(document.documentElement, { childList: true, subtree: true });

  scan();
})();
